import { useEffect, useRef, useState } from 'react';

/**
 * Image aspect ratios (w/h) keyed by URL PATH (query stripped) — presigned S3
 * URLs rotate their signature on every response while the object (and its
 * dimensions) stays the same. Module-level: survives remounts, page flips and
 * crop↔full mode switches, so a layout that depends on aspects is
 * deterministic once an image has been measured ANYWHERE in the session.
 */
const ASPECT_CACHE = new Map<string, number>();

const aspectKey = (url: string) => url.split('?')[0];

/** Record a measured aspect. Returns true when this actually changed the cache
 *  (callers re-render only then — repeated identical reports are free). */
export function primeAspect(url: string, ar: number): boolean {
  if (!(ar > 0) || !Number.isFinite(ar)) return false;
  const key = aspectKey(url);
  const prev = ASPECT_CACHE.get(key);
  if (prev !== undefined && Math.abs(prev - ar) < 0.01) return false;
  ASPECT_CACHE.set(key, ar);
  return true;
}

export function cachedAspect(url: string | null | undefined): number | undefined {
  return url ? ASPECT_CACHE.get(aspectKey(url)) : undefined;
}

/**
 * Eagerly measure the given image URLs (skipping ones already cached) and
 * re-render the caller as results land. Meant for SMALL images (tight crops) —
 * a probe downloads the file; don't pass multi-hundred-KB full frames here
 * (their aspects arrive from the tiles that actually display them, via
 * `primeAspect`). `bump` forces a re-read after an external `primeAspect`.
 */
export function useImageAspects(urls: (string | null | undefined)[]) {
  const [, setVersion] = useState(0);
  const flushTimer = useRef<number | null>(null);
  // COALESCED re-render: on first load a dozen probes resolve within the same
  // second — one layout pass shortly after the burst instead of one per image
  // keeps the grid from visibly reshuffling tile by tile.
  const bump = () => {
    if (flushTimer.current !== null) return;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      setVersion(v => v + 1);
    }, 80);
  };
  useEffect(
    () => () => {
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
    },
    [],
  );

  const keyList = urls
    .filter((u): u is string => !!u)
    .map(aspectKey)
    .join('|');

  useEffect(() => {
    let alive = true;
    for (const u of urls) {
      if (!u || ASPECT_CACHE.has(aspectKey(u))) continue;
      const img = new Image();
      img.onload = () => {
        if (primeAspect(u, img.naturalWidth / Math.max(1, img.naturalHeight)) && alive) {
          bump();
        }
      };
      img.src = u;
    }
    return () => {
      alive = false;
    };
    // keyList captures the url set; `urls` itself is a fresh array every render.
  }, [keyList]);

  return { aspectOf: cachedAspect, bump };
}
